/**
 * The document as the server describes it, and the few decisions the screen
 * makes about laying it out - as plain functions, so Node's test runner can
 * pin them without a bundler.
 *
 * ── THE SHAPE IS THE API'S, MIRRORED ──
 * `GET /v1/invoices/:id/document` returns `DocumentView` from
 * apps/api/src/modules/documents/pdf/document-view.ts: one object the PDF
 * and this screen both draw, so they cannot disagree about a label, a
 * column or a rupee. Every amount arrives as a pair - the decimal string and
 * the string a person reads - and the screen prints `display` and never
 * formats. The types here mirror that file with every field optional,
 * because the screen must not go blank over a field that is null, renamed
 * or not there yet: a document with no tagline is a document, not an error.
 *
 * No imports, on purpose: the same reason `imported.ts` has none.
 */

export interface MoneyCell {
  /** As stored: "59000.00". */
  value?: string | null;
  /** As printed: "₹59,000.00". */
  display?: string | null;
}

export interface MetaRow {
  label?: string | null;
  value?: string | null;
}

export interface LineTaxCell {
  /** "9" - what prints in the column beside the amount. */
  percentage?: string | null;
  amount?: MoneyCell | null;
}

export interface DocumentLine {
  /** 1-based. */
  index?: number | null;
  name?: string | null;
  description?: string | null;
  hsnSac?: string | null;
  /** "2" rather than "2.0000". */
  quantity?: string | null;
  unit?: string | null;
  rate?: MoneyCell | null;
  discount?: MoneyCell | null;
  taxes?: {
    cgst?: LineTaxCell | null;
    sgst?: LineTaxCell | null;
    igst?: LineTaxCell | null;
    other?: Array<LineTaxCell & { name?: string | null }> | null;
  } | null;
  taxTotal?: MoneyCell | null;
  amount?: MoneyCell | null;
}

export interface DocumentParty {
  name?: string | null;
  lines?: string[] | null;
  gstin?: string | null;
  email?: string | null;
}

export interface DocumentLetterhead {
  name?: string | null;
  tagline?: string | null;
  addressLines?: string[] | null;
  gstin?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoFileId?: string | null;
  logoUrl?: string | null;
  /** A palette slot. */
  accentColour?: string | null;
  accentHex?: string | null;
}

export type TaxColumns = 'cgst_sgst' | 'igst' | 'tax' | 'none';

export interface DocumentTotals {
  taxMode?: 'exclusive' | 'inclusive' | null;
  subTotal?: MoneyCell | null;
  discount?: { label?: string | null; amount?: MoneyCell | null } | null;
  shipping?: MoneyCell | null;
  taxes?: Array<{ label?: string | null; amount?: MoneyCell | null }> | null;
  /** Inclusive pricing: the tax rows are inside the sub total already. */
  taxesIncluded?: boolean | null;
  tcs?: { label?: string | null; amount?: MoneyCell | null } | null;
  adjustment?: { label?: string | null; amount?: MoneyCell | null } | null;
  total?: MoneyCell | null;
  /** Null for a proforma and when nothing has been received. */
  paymentsReceived?: MoneyCell | null;
  /** Null for a proforma. */
  balanceDue?: MoneyCell | null;
  reverseCharge?: boolean | null;
}

export interface DocumentJson {
  kind?: 'invoice' | 'proforma' | string | null;
  /** "TAX INVOICE", "INVOICE" or "PROFORMA INVOICE". */
  title?: string | null;
  number?: string | null;
  filename?: string | null;
  status?: string | null;
  currency?: string | null;
  letterhead?: DocumentLetterhead | null;
  meta?: { left?: MetaRow[] | null; right?: MetaRow[] | null } | null;
  originally?: string | null;
  billTo?: DocumentParty | null;
  shipTo?: DocumentParty | null;
  columns?: { hsnSac?: boolean | null; tax?: TaxColumns | null } | null;
  lines?: DocumentLine[] | null;
  totals?: DocumentTotals | null;
  totalInWords?: string | null;
  notes?: string | null;
  terms?: string | null;
  paymentDetails?: MetaRow[] | null;
  /** A UPI intent for the balance due, with its PNG; null when there is nothing to scan for. */
  upiQr?: { uri?: string | null; caption?: string | null; pngDataUrl?: string | null } | null;
  signatory?: { name?: string | null; signatureUrl?: string | null; label?: string | null } | null;
  declarations?: string[] | null;
  /**
   * The rupee view of a foreign-currency document: what GST and FEMA read,
   * from the figures stored when it was issued. Null on a document in the
   * book's own currency.
   */
  baseValue?: BaseValue | null;
}

export interface BaseValue {
  currency?: string | null;
  /** "VALUE IN INR". */
  title?: string | null;
  /** Taxable value, the tax, TCS when any, and the total - last. */
  rows?: Array<{ label?: string | null; amount?: MoneyCell | null }> | null;
  /** "1 GBP = ₹127.8600 · FBIL reference rate for 23-09-2026". */
  rate?: string | null;
  /** "Stated for GST and FEMA purposes. The amount payable is £880.00." */
  note?: string | null;
}

/** The book's own address fields, as `GET /v1/settings/book` carries them. */
export interface BookAddress {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface PaymentDetails {
  bankName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  swift?: string | null;
  branch?: string | null;
  upiId?: string | null;
}

/** A proforma's document owes nothing; the screen labels its dates and totals accordingly. */
export function isProforma(doc: Pick<DocumentJson, 'kind' | 'title'> | null | undefined): boolean {
  return doc?.kind === 'proforma' || /proforma/i.test(doc?.title ?? '');
}

/**
 * Which tax columns the lines table draws - the server's answer, with the
 * lines themselves as the fallback for a response that predates `columns`.
 * A column of dashes on an intra-state sale, or a missing IGST on an
 * inter-state one, prints on a customer's invoice; this is where it is
 * decided once.
 */
export function taxColumns(doc: Pick<DocumentJson, 'columns' | 'lines'> | null | undefined): TaxColumns {
  const declared = doc?.columns?.tax;
  if (declared === 'cgst_sgst' || declared === 'igst' || declared === 'tax' || declared === 'none') return declared;
  const lines = doc?.lines ?? [];
  if (lines.some((line) => line.taxes?.cgst || line.taxes?.sgst)) return 'cgst_sgst';
  if (lines.some((line) => line.taxes?.igst)) return 'igst';
  if (lines.some((line) => (line.taxes?.other ?? []).length > 0)) return 'tax';
  return 'none';
}

/** Whether the HSN/SAC column prints: the server's say, else whether any line carries one. */
export function showsHsnSac(doc: Pick<DocumentJson, 'columns' | 'lines'> | null | undefined): boolean {
  if (typeof doc?.columns?.hsnSac === 'boolean') return doc.columns.hsnSac;
  return (doc?.lines ?? []).some((line) => hasText(line.hsnSac));
}

/** "2 hrs" from the server's trimmed quantity and the unit; the quantity alone without one. */
export function quantityText(line: Pick<DocumentLine, 'quantity' | 'unit'> | null | undefined): string {
  const quantity = line?.quantity?.trim() ?? '';
  const unit = line?.unit?.trim() ?? '';
  return unit ? `${quantity} ${unit}`.trim() : quantity;
}

/** "9%" from the cell's percentage; empty when there is none. */
export function percentText(percentage: string | number | null | undefined): string {
  if (percentage === null || percentage === undefined || percentage === '') return '';
  return `${percentage}%`;
}

/** What an amount prints as: the server's display string, falling back to the raw value. */
export function moneyText(cellValue: MoneyCell | null | undefined): string {
  return cellValue?.display ?? cellValue?.value ?? '';
}

/**
 * The book's address, as the lines it prints on - the settings preview's
 * copy of what the server's `letterheadOf` does, so the preview is the
 * letterhead before the first document exists.
 *
 * Three short lines - the street, then "City, State PIN", then the country
 * - never six fields each on its own line. Empty fields drop out rather
 * than leaving a dangling comma; "India" alone is still a line, since a
 * foreign customer reads it.
 */
export function addressLines(book: BookAddress | null | undefined): string[] {
  if (!book) return [];
  const clean = (v: string | null | undefined) => v?.trim() ?? '';
  const lines: string[] = [];
  if (clean(book.addressLine1)) lines.push(clean(book.addressLine1));
  if (clean(book.addressLine2)) lines.push(clean(book.addressLine2));
  const cityState = [clean(book.city), clean(book.state)].filter(Boolean).join(', ');
  const locality = [cityState, clean(book.postalCode)].filter(Boolean).join(' ');
  if (locality) lines.push(locality);
  if (clean(book.country)) lines.push(clean(book.country));
  return lines;
}

/** "hello@acme.in · +91 98400 00000 · acme.in" - whatever is filled in, in that order. */
export function contactLine(contact: { email?: string | null; phone?: string | null; website?: string | null } | null | undefined): string {
  if (!contact) return '';
  return [contact.email, contact.phone, contact.website]
    .map((v) => v?.trim() ?? '')
    .filter(Boolean)
    .join(' · ');
}

/** The rows of the "Payment details" block from the settings blob, in reading order, empties dropped. */
export function paymentDetailRows(details: PaymentDetails | null | undefined): Array<{ label: string; value: string }> {
  if (!details) return [];
  const rows: Array<[string, string | null | undefined]> = [
    ['Bank', details.bankName],
    ['Account name', details.accountName],
    ['Account number', details.accountNumber],
    ['IFSC', details.ifsc],
    ['SWIFT', details.swift],
    ['Branch', details.branch],
    ['UPI', details.upiId],
  ];
  return rows
    .map(([label, value]) => ({ label, value: value?.trim() ?? '' }))
    .filter((row) => row.value.length > 0);
}

/**
 * Where the PDF is. The org is IN THE PATH, because this is a link the
 * browser navigates to in a new tab, and a navigation cannot carry the
 * `x-org-id` header - see docs/coding-standards.md, "A download cannot
 * carry a header". The route accepts both forms; the SPA uses this one.
 */
export function pdfUrl(path: 'invoices' | 'proformas', orgId: string, id: string): string {
  return `/v1/${path}/${orgId}/${id}/pdf`;
}

/**
 * Whether a section prints at all. A "Payment details" heading over nothing,
 * or "Notes" over an empty string, is the tell of a template; a document
 * only says what it has to say.
 */
export function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}
